import { captureException } from '@sentry/react';

/** Context for AI: every browser-storage key Spotlight (and AccountSwitcher) uses. `storage/` is the only module that touches browser storage, except `helpers/statelessFiltersFromSessionStorage.ts`, a dependency-free copy of paramount's reader. */
export const STORAGE_KEYS = {
	nlsSearchOn: {
		area: 'local',
		key: 'isNLSSearchOn',
		fallback: false,
		decode: (parsed: unknown) => Boolean(parsed),
	},
	// Read by paramount (boundary/hybridSearchToggle.ts), which also defaults it to ON and treats
	// anything but `false` as ON. Both sides must agree.
	hybridSearchOn: {
		area: 'local',
		key: 'isHybridSearchOn',
		fallback: true,
		decode: (parsed: unknown) => parsed !== false,
	},
	// Written by paramount. Read only via the dependency-free copy, so a corrupt value falls back to
	// `[]` without a Sentry report.
	activeFilters: { area: 'session', key: 'activeFilters' },
	// Shared with paramount's own recent-searches implementation.
	recentSearches: { area: 'indexedDB', key: 'BynderRecentSearches' },
	recentSearchesNLS: { area: 'indexedDB', key: 'BynderRecentSearchesNLS' },
	// A prefix: paramount reads the uploaded image from `<prefix>_<uuid>`.
	searchByImageUpload: { area: 'indexedDB', key: 'BynderSearchByImageUpload' },
} as const;

type StorageName = keyof typeof STORAGE_KEYS;
type NameInArea<Area> = {
	[Name in StorageName]: (typeof STORAGE_KEYS)[Name]['area'] extends Area ? Name : never;
}[StorageName];

type LocalStorageName = NameInArea<'local'>;
type WebStorageName = NameInArea<'local' | 'session'>;

const getWebStorage = (name: WebStorageName) =>
	STORAGE_KEYS[name].area === 'local' ? localStorage : sessionStorage;

export const reportStorageError = (error: unknown, key: string) => {
	captureException(error, { extra: { key } });
};

export const readStorage = (name: LocalStorageName): boolean => {
	const { key, fallback, decode } = STORAGE_KEYS[name];

	try {
		const storedValue = localStorage.getItem(key);

		return storedValue === null ? fallback : decode(JSON.parse(storedValue));
	} catch (error) {
		reportStorageError(error, key);

		return fallback;
	}
};

export const writeStorage = (name: LocalStorageName, value: boolean) => {
	const { key } = STORAGE_KEYS[name];

	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		reportStorageError(error, key);
	}
};

export const removeFromStorage = (name: WebStorageName) => {
	const { key } = STORAGE_KEYS[name];

	try {
		getWebStorage(name).removeItem(key);
	} catch (error) {
		reportStorageError(error, key);
	}
};
